import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Divider,
  LinearProgress
} from '@mui/material';
import axiosInstance from '../utils/axios';

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Started', value: 'started' },
  { label: 'Cancelled', value: 'cancelled' },
];

export default function AdminJobs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState('');
  const [username, setUsername] = useState('');
  const [truckershubId, setTruckershubId] = useState('');
  const [jobID, setJobID] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [unmapped, setUnmapped] = useState(false);

  // Import progress + dead letters
  const [progress, setProgress] = useState(null);
  const [deadLetters, setDeadLetters] = useState([]);
  const [deadLettersTotal, setDeadLettersTotal] = useState(0);
  const [deadLettersPage, setDeadLettersPage] = useState(1);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [olderThanMonths, setOlderThanMonths] = useState('');
  const [withinLastMonths, setWithinLastMonths] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [deletePreview, setDeletePreview] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/jobs', {
        params: {
          page,
          limit,
          status: status || undefined,
          username: username || undefined,
          truckershubId: truckershubId || undefined,
          jobID: jobID || undefined,
          unmapped: unmapped || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelectedJobIds([]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  useEffect(() => {
    // refetch on filter changes
    setPage(1);
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, username, truckershubId, jobID, dateFrom, dateTo, unmapped]);

  const fetchProgress = async () => {
    try {
      const { data } = await axiosInstance.get('/jobs/import/progress');
      setProgress(data);
    } catch (e) { console.error(e); }
  };

  const fetchDeadLetters = async () => {
    try {
      const { data } = await axiosInstance.get('/jobs/deadletters', { params: { page: deadLettersPage, limit: 10 } });
      setDeadLetters(data.items || []);
      setDeadLettersTotal(data.total || 0);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchProgress();
    fetchDeadLetters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadLettersPage]);

  const applyFilters = () => {
    setPage(1);
    fetchJobs();
  };

  const handleDeletePreview = async () => {
    try {
      const { data } = await axiosInstance.delete('/jobs', {
        data: {
          status: status || undefined,
          username: username || undefined,
          truckershubId: truckershubId || undefined,
          jobID: jobID || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          olderThanMonths: olderThanMonths ? Number(olderThanMonths) : undefined,
          withinLastMonths: withinLastMonths ? Number(withinLastMonths) : undefined,
          dryRun: true,
        },
      });
      setDeletePreview(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const { data } = await axiosInstance.delete('/jobs', {
        data: {
          status: status || undefined,
          username: username || undefined,
          truckershubId: truckershubId || undefined,
          jobID: jobID || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          olderThanMonths: olderThanMonths ? Number(olderThanMonths) : undefined,
          withinLastMonths: withinLastMonths ? Number(withinLastMonths) : undefined,
          dryRun: false,
        },
      });
      setDeletePreview(data);
      fetchJobs();
    } catch (e) {
      console.error(e);
    }
  };

  const runImport = async (dry = true) => {
    try {
      await axiosInstance.post('/jobs/import/run', { dryRun: dry });
      await fetchProgress();
      await fetchJobs();
    } catch (e) { console.error(e); }
  };

  const linkAll = async () => {
    try {
      await axiosInstance.post('/jobs/link/all', { dryRun: false });
      await fetchProgress();
      await fetchJobs();
    } catch (e) { console.error(e); }
  };

  const retryDeadLetter = async (id) => {
    try {
      await axiosInstance.post(`/jobs/import/retry/${id}`);
      await fetchProgress();
      await fetchDeadLetters();
    } catch (e) { console.error(e); }
  };

  const reconcileForDivision = async (job) => {
    try {
      await axiosInstance.post(`/jobs/${job._id}/reconcile-division`, {
        force: true,
      });
      await fetchJobs();
      setBulkResult({
        reconciled: 1,
        skippedAlreadyApplied: 0,
        skippedMissingMapping: 0,
        skippedNonDelivered: 0,
        failed: 0,
      });
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed to reconcile division stats');
    }
  };

  const toggleOne = (jobId) => {
    setSelectedJobIds((prev) => (
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    ));
  };

  const toggleAllOnPage = () => {
    const pageIds = items.map((j) => j._id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedJobIds.includes(id));
    if (allSelected) {
      setSelectedJobIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedJobIds((prev) => [...new Set([...prev, ...pageIds])]);
  };

  const runBulkReconcile = async () => {
    if (!selectedJobIds.length) return;
    if (!window.confirm(`Reconcile ${selectedJobIds.length} selected job(s) for division stats?`)) return;
    try {
      const { data } = await axiosInstance.post('/jobs/reconcile-division/bulk', {
        jobIds: selectedJobIds,
        force: true,
      });
      setBulkResult(data);
      await fetchJobs();
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed to bulk reconcile jobs');
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Admin • Job Management</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="error" onClick={() => { setDeleteOpen(true); setDeletePreview(null); }}>Bulk Delete…</Button>
          <Button variant="contained" onClick={runBulkReconcile} disabled={!selectedJobIds.length}>
            Reconcile Selected ({selectedJobIds.length})
          </Button>
        </Stack>
      </Stack>
      {bulkResult && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Reconcile result: reconciled {bulkResult.reconciled || 0}, already applied {bulkResult.skippedAlreadyApplied || 0},
          missing mapping {bulkResult.skippedMissingMapping || 0}, non-delivered {bulkResult.skippedNonDelivered || 0},
          failed {bulkResult.failed || 0}.
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField select label="Status" fullWidth size="small" value={status} onChange={(e) => setStatus(e.target.value)}>
                {statusOptions.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Username" fullWidth size="small" value={username} onChange={(e) => setUsername(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="TruckersHub ID"
                fullWidth
                size="small"
                value={truckershubId}
                onChange={(e) => setTruckershubId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Job ID"
                type="number"
                fullWidth
                size="small"
                value={jobID}
                onChange={(e) => setJobID(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField type="date" label="From" fullWidth size="small" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField type="date" label="To" fullWidth size="small" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={applyFilters}>Apply</Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setStatus('');
                    setUsername('');
                    setTruckershubId('');
                    setJobID('');
                    setDateFrom('');
                    setDateTo('');
                    setPage(1);
                  }}
                >
                  Reset
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel control={<Checkbox checked={unmapped} onChange={(e) => setUnmapped(e.target.checked)} />} label="Show unmapped only" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Import Progress */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>Legacy Import • Progress</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => runImport(true)}>Dry Run</Button>
              <Button size="small" variant="contained" onClick={() => runImport(false)}>Run Import</Button>
             
              <Button size="small" onClick={fetchProgress}>Refresh</Button>
            </Stack>
          </Stack>
          {progress?.progress && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Import Status: {progress.progress.running ? 'Running' : 'Idle'} • {progress.progress.processed}/{progress.progress.total} ({progress.progress.percent}%)
              </Typography>
              <LinearProgress variant="determinate" value={progress.progress.percent} />
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><Typography variant="body2" color="text.secondary">Legacy Total</Typography><Typography variant="h6">{progress?.legacyTotal ?? '-'}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="body2" color="text.secondary">Imported Jobs</Typography><Typography variant="h6">{progress?.jobsTotal ?? '-'}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="body2" color="text.secondary">Unmapped Jobs</Typography><Typography variant="h6">{progress?.unmappedJobs ?? '-'}</Typography></Grid>
            <Grid item xs={6} md={3}><Typography variant="body2" color="text.secondary">Dead Letters</Typography><Typography variant="h6">{progress?.deadLetters ?? '-'}</Typography></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>Jobs</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField select size="small" label="Per page" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} sx={{ width: 120 }}>
                {[10,20,50,100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </TextField>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
            </Stack>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={items.length > 0 && items.every((j) => selectedJobIds.includes(j._id))}
                      indeterminate={items.some((j) => selectedJobIds.includes(j._id)) && !items.every((j) => selectedJobIds.includes(j._id))}
                      onChange={toggleAllOnPage}
                    />
                  </TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>From → To</TableCell>
                  <TableCell align="right">Distance</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((j) => (
                  (() => {
                    const revenueValue = Number(j?.normalized?.revenueValue);
                    const displayRevenue = Number.isFinite(revenueValue)
                      ? revenueValue
                      : Number(j?.revenue ?? j?.income ?? 0);
                    return (
                  <TableRow key={j._id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedJobIds.includes(j._id)} onChange={() => toggleOne(j._id)} />
                    </TableCell>
                    <TableCell>{j.jobID || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={j.status} color={j.status === 'delivered' ? 'success' : (j.status === 'started' ? 'warning' : 'default')} variant="outlined" />
                    </TableCell>
                    <TableCell>{j.driver?.username || '-'}</TableCell>
                    <TableCell>{j.source?.city?.name || '-'} → {j.destination?.city?.name || '-'}</TableCell>
                    <TableCell align="right">{j.distanceDriven || 0}</TableCell>
                    <TableCell align="right">${Math.round(displayRevenue).toLocaleString()}</TableCell>
                    <TableCell>{new Date(j.createdAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => reconcileForDivision(j)}>
                        Reconcile for division
                      </Button>
                    </TableCell>
                  </TableRow>
                    );
                  })()
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary">No jobs found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" />
          </Stack>
        </CardContent>
      </Card>

      {/* Dead Letters */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>Dead Letters</Typography>
            <Pagination count={Math.max(1, Math.ceil(deadLettersTotal / 10))} page={deadLettersPage} onChange={(_, p) => setDeadLettersPage(p)} shape="rounded" size="small" />
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>jobID</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deadLetters.map(dl => (
                  <TableRow key={dl._id} hover>
                    <TableCell>{dl.jobID || '-'}</TableCell>
                    <TableCell>{dl.reason || '-'}</TableCell>
                    <TableCell>{dl.error || '-'}</TableCell>
                    <TableCell>{new Date(dl.createdAt).toLocaleString()}</TableCell>
                    <TableCell align="right"><Button size="small" variant="contained" onClick={() => retryDeadLetter(dl._id)}>Retry</Button></TableCell>
                  </TableRow>
                ))}
                {deadLetters.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary">No dead letters.</Typography></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Delete Jobs</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">Choose a quick rule or date range. You can preview the count before deleting.</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField type="number" label="Older than (months)" fullWidth size="small" value={olderThanMonths} onChange={(e) => setOlderThanMonths(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField type="number" label="Within last (months)" fullWidth size="small" value={withinLastMonths} onChange={(e) => setWithinLastMonths(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField type="date" label="From" fullWidth size="small" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField type="date" label="To" fullWidth size="small" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField select label="Status (optional)" fullWidth size="small" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {statusOptions.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Username (optional)" fullWidth size="small" value={username} onChange={(e) => setUsername(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="TruckersHub ID (optional)"
                  fullWidth
                  size="small"
                  value={truckershubId}
                  onChange={(e) => setTruckershubId(e.target.value)}
                />
              </Grid>
            </Grid>
            <Divider />
            <FormControlLabel control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />} label="Dry run (preview only)" />
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleDeletePreview}>Preview Count</Button>
              <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={dryRun}>Delete</Button>
            </Stack>
            {deletePreview && (
              <Typography variant="body2">Matched: {deletePreview.matchedCount} • Deleted: {deletePreview.deletedCount} {deletePreview.dryRun ? '(dry run)' : ''}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


